/*
 * Copyright (c) Nexign, JSC, 2022-2023.
 */
/**
 *
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.22.0
 */

import pickBy from 'lodash/pickBy';

import type { IAccountInfoRequestDeliveryData } from '../types';
import type { IAccountDeliveryPageInfo } from '../types/pageAccountInfoGeneralTypes';
import type {
  IAccountDeliveryInfoData,
  IAccountInfoDeliveryData,
} from '../types/pageAccountInfoTypes';

type MapResponseToMeta = (
  meta: IAccountDeliveryPageInfo,
) => IAccountDeliveryInfoData;
type MapFormDataToUpdateRequest = (
  data: IAccountInfoDeliveryData,
) => IAccountInfoRequestDeliveryData;

export const mapResponseToMeta: MapResponseToMeta = (meta) => {
  const { deliveryInfoData } = meta;

  const {
    deliveryData,
    deliveryCountryId,
    edm,
    post,
    isAgreementEditingWarningShown,
  } = deliveryInfoData;

  const initialDeliveryData = {
    address: {
      ...deliveryData.address,
      addressId: deliveryData.address?.addressId ?? '',
      addressString: deliveryData.address?.addressString ?? '',
    },
    airId: String(deliveryData.airId) ?? '',
    contactPerson: deliveryData.contactPerson ?? '',
    email: deliveryData.email ?? '',
    note: deliveryData.note ?? '',
    phone: deliveryData.phone ?? '',
    useCourier: deliveryData.useCourier ?? false,
    useEdm: deliveryData.useEdm ?? false,
    useEmail: deliveryData.useEmail ?? false,
    usePost: deliveryData.usePost ?? false,
  };

  return {
    deliveryCountryId,
    deliveryData: initialDeliveryData,
    edm,
    isAgreementEditingWarningShown,
    post,
  };
};

export const mapFormDataToUpdateRequest: MapFormDataToUpdateRequest = (
  data,
) => {
  const { address, ...rest } = data;

  const predicate = (value: unknown) => {
    if (
      value === null ||
      value === undefined ||
      (typeof value === 'string' && !value.length)
    ) {
      return false;
    }
    return true;
  };

  let cleanAddress = pickBy(address, predicate);
  if (cleanAddress.apartment) {
    const { def } = cleanAddress.apartment;
    const apartmentNumber = def.split(' ').find((item) => Number(item));
    if (apartmentNumber) {
      cleanAddress = {
        ...cleanAddress,
        apartment: {
          ...cleanAddress.apartment,
          def: apartmentNumber,
        },
      };
    }
  }

  const deliveryData: IAccountInfoRequestDeliveryData = pickBy(rest, predicate);

  if (!!Object.values(cleanAddress).length) {
    deliveryData.address = cleanAddress;
  }

  return deliveryData;
};
